import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { IntegrationNoAccess } from '@/components/integration-no-access';
import { StepCard } from '@/components/integration-step';
import {
  buildPivot,
  countDecisions,
  decisionOf,
  describeImport,
  formatCount,
  formatHours,
  importBlockedReason,
  isBlockingReason,
  isMonth,
  monthLabel,
  pivotToTsv,
  plural,
  rangeFromMonths,
  skipReasonLabel,
  SKIP_OPTION,
  valueOf,
} from '@/features/time-tracker/lib/clockify-import-view';
import {
  useClockifyFiles,
  useClockifyMappings,
  useClockifyPreview,
  useClockifyRuns,
  useClockifyStatus,
  useConnectClockify,
  useDisconnectClockify,
  useRunClockifyImport,
  useSaveClockifyMappings,
  useTestClockify,
  useUndoClockifyRun,
  type ClockifyMappingOptions,
  type ClockifyStatus,
  type ImportPayload,
  type PreviewPayload,
  type SaveMappingsPayload,
} from '@/lib/clockify-import';
import { formatDateTime } from '@/lib/date';
import { extractErrorMessage } from '@/lib/errors';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type {
  ClockifyImportPreview,
  ClockifyImportRunItem,
  ClockifyProjectMapping,
  ClockifyRowSource,
  ClockifyUserMapping,
} from '@hygge/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronRight,
  Clipboard,
  Download,
  Info,
  Loader2,
  RotateCcw,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

/**
 * The Clockify migration screen.
 *
 * WHY IT IS A SCREEN AND NOT A SCRIPT: Clockify has its own project names and
 * its own user list, and no rule can turn either into ours. A person has to say
 * which project is which, and who the one unmatched address belongs to. Those
 * two answers are also what unblocks the import at all — `project_id` is NOT
 * NULL on our side.
 *
 * THE ORDER MATTERS AND NOTHING IS WRITTEN UNTIL THE LAST STEP. Connect, choose
 * a range, map, read the preview, and only then import. The preview is the
 * screen's centre of gravity: it is the number the owner compares against
 * Clockify's own report before six months of other people's hours are written.
 *
 * Clockify is retired once this has run. There is no schedule and no polling
 * anywhere in this file, and nothing here should grow one.
 */

const routePath = '/settings/integrations/clockify' as const;
export const Route = createFileRoute(routePath)({
  component: ClockifyGate,
});

function ClockifyGate() {
  // Two permissions, mirroring the controller: `time.import.view` opens every
  // read route (status, mappings, preview, runs) and `time.import` the writes.
  // A view-only reader gets the whole screen minus its buttons — verifying the
  // migrated totals is a job of its own, and the grants pair run with view so
  // an importer is never view-less.
  const canRun = usePermission('time.import');
  const canView = usePermission('time.import.view');
  if (!canRun && !canView) return <IntegrationNoAccess />;
  return <ClockifyImportPage readOnly={!canRun} />;
}

const API_KEY_URL = 'https://app.clockify.me/user/settings';

/** A dropdown value per Clockify id: '' = undecided, `SKIP_OPTION` = skip, anything else = our id. */
type DraftMap = Record<string, string>;

function ClockifyImportPage({ readOnly }: { readOnly: boolean }) {
  const { data: status, isLoading: statusLoading } = useClockifyStatus();
  const files = useClockifyFiles();

  const [source, setSource] = useState<ClockifyRowSource>('files');
  const [fromMonth, setFromMonth] = useState('');
  const [toMonth, setToMonth] = useState('');

  /**
   * The preview being displayed, and the exact payload that produced it.
   *
   * Held here rather than read off the mutation because a failed re-scan must
   * not leave the previous range's numbers on screen looking current, and
   * because the import is sent with THIS payload — the run is the preview,
   * carried out.
   */
  const [preview, setPreview] = useState<ClockifyImportPreview | null>(null);
  const [previewPayload, setPreviewPayload] = useState<PreviewPayload | null>(null);
  const [projectDraft, setProjectDraft] = useState<DraftMap>({});
  const [userDraft, setUserDraft] = useState<DraftMap>({});
  const [run, setRun] = useState<ClockifyImportRunItem | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);

  const previewMutation = useClockifyPreview();
  const importMutation = useRunClockifyImport();

  const workspaceId = preview?.workspaceId ?? status?.workspaceId ?? null;
  const mappings = useClockifyMappings(workspaceId);
  const saveMappings = useSaveClockifyMappings(workspaceId);

  const inventory = files.data;
  const diskMonths = inventory?.months ?? [];

  // Defaulting the range to the export on disk, once, and only if the owner has
  // not chosen anything. Anything else is a guess about a workspace we can see
  // the truth of.
  const defaultFrom = diskMonths[0]?.month ?? '';
  const defaultTo = diskMonths[diskMonths.length - 1]?.month ?? '';
  const effectiveFrom = fromMonth || defaultFrom;
  const effectiveTo = toMonth || defaultTo;
  const rangeValid = isMonth(effectiveFrom) && isMonth(effectiveTo) && effectiveFrom <= effectiveTo;

  /** Changing the question invalidates the answer. */
  const resetPreview = () => {
    setPreview(null);
    setPreviewPayload(null);
    setProjectDraft({});
    setUserDraft({});
    setRun(null);
    setRunNote(null);
  };

  const runPreview = (payloadOverride?: PreviewPayload) => {
    const payload: PreviewPayload = payloadOverride ?? { ...rangeFromMonths(effectiveFrom, effectiveTo), source };
    previewMutation.mutate(payload, {
      onSuccess: (data) => {
        setPreview(data);
        setPreviewPayload(payload);
        setProjectDraft({});
        setUserDraft({});
      },
    });
  };

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-6xl space-y-6 px-6 py-8">
        <PageIntro />

        {readOnly && (
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>
              View only — you can read the status, the mappings as decided, the preview totals and
              the run history. Running the import needs the “Import time entries” permission.
            </p>
          </div>
        )}

        <ConnectStep status={status} isLoading={statusLoading} readOnly={readOnly} />

        <RangeStep
          source={source}
          onSourceChange={(next) => {
            setSource(next);
            resetPreview();
          }}
          connected={status?.connected === true}
          files={{ data: inventory, isLoading: files.isLoading, isError: files.isError, error: files.error }}
          fromMonth={effectiveFrom}
          toMonth={effectiveTo}
          onFromChange={(v) => {
            setFromMonth(v);
            resetPreview();
          }}
          onToChange={(v) => {
            setToMonth(v);
            resetPreview();
          }}
          rangeValid={rangeValid}
          scanning={previewMutation.isPending}
          onScan={() => runPreview()}
          scanError={previewMutation.isError ? extractErrorMessage(previewMutation.error, 'Could not read the range') : null}
          preview={preview}
        />

        <MapStep
          preview={preview}
          options={mappings.data?.options}
          optionsLoading={mappings.isLoading}
          optionsError={mappings.isError ? extractErrorMessage(mappings.error, 'Could not load projects and employees') : null}
          projectDraft={projectDraft}
          userDraft={userDraft}
          onProjectChange={(id, value) => setProjectDraft((d) => ({ ...d, [id]: value }))}
          onUserChange={(id, value) => setUserDraft((d) => ({ ...d, [id]: value }))}
          saving={saveMappings.isPending}
          readOnly={readOnly}
          saveError={saveMappings.isError ? extractErrorMessage(saveMappings.error, 'Could not save the mapping') : null}
          onSave={(payload) =>
            saveMappings.mutate(payload, {
              // The numbers on screen were computed under the old decisions.
              // Re-reading is free on the files source and cached on the API one.
              onSuccess: () => runPreview(previewPayload ?? undefined),
            })
          }
        />

        <ImportStep
          preview={preview}
          payload={previewPayload}
          readOnly={readOnly}
          importing={importMutation.isPending}
          importError={importMutation.isError ? extractErrorMessage(importMutation.error, 'The import did not run') : null}
          onImport={(payload) => {
            importMutation.mutate(payload, {
              onSuccess: (result) => {
                setRun(result.run);
                setRunNote(
                  result.alreadyComplete
                    ? 'Every entry in this range was already imported by an earlier run. Nothing was written.'
                    : null,
                );
                // The re-scan proves it: a second look at the same range should
                // now report every row as already imported. Sent as the range
                // that produced this preview, without the digest that answered
                // it — a preview asks a question and quotes nothing.
                runPreview(previewPayload ?? undefined);
              },
            });
          }}
          run={run}
          runNote={runNote}
          onUndone={() => runPreview(previewPayload ?? undefined)}
        />

        <HistorySection onUndone={() => runPreview(previewPayload ?? undefined)} readOnly={readOnly} />
      </div>
    </div>
  );
}

// ─── Intro ─────────────────────────────────────────────────────────────────

function PageIntro() {
  return (
    <div className="space-y-4">
      <Link
        to="/settings"
        search={{ tab: 'integrations' }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Integrations
      </Link>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">Clockify Import</h1>
        <p className="text-sm text-muted-foreground mt-1">
          A one-time migration of Clockify time entries into the Time Tracker. Nothing is written
          until the last step.
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2.5">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            Clockify keeps its own project names and its own user list. Mapping them onto ours is a
            judgement call, so this page asks rather than guesses — and the import stays blocked
            until every project and person has an answer.
          </p>
          <p>
            Imported entries are marked <span className="font-mono text-[11px]">clockify</span> and
            carry the Clockify id they came from, so a second run writes nothing and any run can be
            undone.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Step 1 · Connect ──────────────────────────────────────────────────────

function ConnectStep({
  status,
  isLoading,
  readOnly,
}: {
  status: ClockifyStatus | undefined;
  isLoading: boolean;
  readOnly: boolean;
}) {
  const connect = useConnectClockify();
  const test = useTestClockify();
  const disconnect = useDisconnectClockify();
  const [workspaceId, setWorkspaceId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (isLoading) {
    return (
      <StepCard step={1} title="Connect Clockify" state="todo">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking connection...
        </div>
      </StepCard>
    );
  }

  if (status?.connected) {
    return (
      <>
        <StepCard
          step={1}
          title="Connect Clockify"
          state="done"
          description={`${status.workspaceName ?? status.workspaceId} · ${status.userCount ?? '?'} users · ${status.timeZone ?? 'zone unknown'}`}
          action={
            readOnly ? undefined : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer gap-1.5"
                disabled={test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Test connection
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                disabled={disconnect.isPending}
                onClick={() => setConfirmOpen(true)}
              >
                {disconnect.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Disconnect'}
              </Button>
            </div>
            )
          }
        >
          {test.isError && (
            <InlineError message={extractErrorMessage(test.error, 'Clockify refused the stored credentials')} />
          )}
          {test.data && (
            <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
                <span className="font-medium text-foreground">{test.data.workspaceName}</span>
                <span className="text-muted-foreground">
                  {test.data.plan ? `${test.data.plan} plan` : 'plan not reported'} ·{' '}
                  {formatCount(test.data.userCount)} users ({formatCount(test.data.activeUserCount)} active) ·{' '}
                  {test.data.timeZone}
                </span>
              </p>
              <p className="mt-1.5 text-muted-foreground">
                {test.data.plan === 'FREE'
                  ? 'On the Free plan a report window is capped at 31 days and the workspace at roughly 30 requests an hour, so a live pull is fetched one calendar month at a time.'
                  : 'Live pulls are fetched one calendar month at a time regardless of plan.'}
              </p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Last checked {status.lastCheckedAt ? formatDateTime(status.lastCheckedAt) : 'never'}. The
            API key is stored encrypted on the server and is never returned by any endpoint.
          </p>
        </StepCard>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disconnect Clockify?</AlertDialogTitle>
              <AlertDialogDescription>
                The stored API key is deleted. Imported time entries, your mapping decisions and the
                run history are all kept — and a rehearsal from the export files on disk still works
                without a connection.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmOpen(false);
                  disconnect.mutate();
                }}
              >
                Disconnect
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  const canSubmit = workspaceId.trim().length > 0 && apiKey.trim().length > 0 && !connect.isPending;

  if (readOnly) {
    return (
      <StepCard step={1} title="Connect Clockify" state="todo">
        <p className="text-sm text-muted-foreground">
          Not connected. Connecting needs the “Import time entries” permission.
        </p>
      </StepCard>
    );
  }

  return (
    <StepCard
      step={1}
      title="Connect Clockify"
      state="todo"
      description="Only needed for a live pull. A rehearsal from the export files already on the server needs no connection at all."
    >
      <ol className="space-y-1 text-xs text-muted-foreground">
        <li>
          1. Open{' '}
          <a href={API_KEY_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            Clockify → Profile settings
          </a>{' '}
          and generate an API key.
        </li>
        <li>2. Copy the workspace id out of the Clockify URL — the 24-character value after /workspaces/.</li>
        <li>3. Paste both below. The key is tested against Clockify before anything is stored.</li>
      </ol>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          connect.mutate(
            { workspaceId: workspaceId.trim(), apiKey: apiKey.trim() },
            // Never readable back — drop it from the page the moment it is stored.
            { onSuccess: () => setApiKey('') },
          );
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="clockify-workspace" className="text-xs font-medium text-muted-foreground">
              Workspace id
            </label>
            <Input
              id="clockify-workspace"
              placeholder="63cebdbb8206496ce417c4e4"
              autoComplete="off"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              disabled={connect.isPending}
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="clockify-api-key" className="text-xs font-medium text-muted-foreground">
              API key
            </label>
            <Input
              id="clockify-api-key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={connect.isPending}
            />
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          The key is stored encrypted on the server and never shown again — not here, not in any API
          response. To replace it, paste a new one.
        </p>

        {connect.isError && <InlineError message={extractErrorMessage(connect.error, 'Could not connect')} />}

        <Button type="submit" size="sm" className="gap-1.5 cursor-pointer" disabled={!canSubmit}>
          {connect.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {connect.isPending ? 'Testing connection...' : 'Test & connect'}
        </Button>
      </form>
    </StepCard>
  );
}

// ─── Step 2 · Range ────────────────────────────────────────────────────────

function RangeStep({
  source,
  onSourceChange,
  connected,
  files,
  fromMonth,
  toMonth,
  onFromChange,
  onToChange,
  rangeValid,
  scanning,
  onScan,
  scanError,
  preview,
}: {
  source: ClockifyRowSource;
  onSourceChange: (next: ClockifyRowSource) => void;
  connected: boolean;
  files: {
    data: { directory: string; exists: boolean; months: Array<{ month: string; rows: number; seconds: number; pulledAt: string | null }>; warnings: string[] } | undefined;
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };
  fromMonth: string;
  toMonth: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
  rangeValid: boolean;
  scanning: boolean;
  onScan: () => void;
  scanError: string | null;
  preview: ClockifyImportPreview | null;
}) {
  const months = files.data?.months ?? [];
  const diskRows = months.reduce((n, m) => n + m.rows, 0);
  const diskSeconds = months.reduce((n, m) => n + m.seconds, 0);

  return (
    <StepCard
      step={2}
      title="Choose the range"
      state={preview ? 'done' : 'todo'}
      description="Whole months. Nothing is read from Clockify or written anywhere until you press Scan."
    >
      {/* ── Where the rows come from ── */}
      <div className="grid gap-2 sm:grid-cols-2">
        <SourceChoice
          selected={source === 'files'}
          onSelect={() => onSourceChange('files')}
          title="Export files on disk"
          detail={
            files.isLoading
              ? 'Looking...'
              : months.length > 0
                ? `${months.length} month${months.length === 1 ? '' : 's'} · ${formatCount(diskRows)} entries · ${formatHours(diskSeconds)} h`
                : 'Nothing pulled yet'
          }
          note="Free, repeatable, and identical every time. The right choice for a rehearsal."
          disabled={false}
        />
        <SourceChoice
          selected={source === 'api'}
          onSelect={() => connected && onSourceChange('api')}
          title="Live pull from Clockify"
          detail={connected ? 'Fetched one calendar month at a time' : 'Needs a connection'}
          note="Spends requests against the workspace's hourly budget. Results are cached for 15 minutes so the preview and the import see the same rows."
          disabled={!connected}
        />
      </div>

      {/* ── What is on disk ── */}
      {source === 'files' && (
        <div className="rounded-md border border-border">
          {files.isLoading && (
            <p className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading the export directory...
            </p>
          )}
          {files.isError && (
            <div className="p-3">
              <InlineError message={extractErrorMessage(files.error, 'Could not read the export directory')} />
            </div>
          )}
          {files.data && months.length === 0 && (
            <p className="p-3 text-sm text-muted-foreground">
              No export files in <span className="font-mono text-xs">{files.data.directory}</span>. Run{' '}
              <span className="font-mono text-xs">scripts/clockify-export.ts --apply</span> on the server, or pull
              live from Clockify instead.
            </p>
          )}
          {months.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[30rem] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Month</th>
                    <th className="px-3 py-2 text-right font-medium">Entries</th>
                    <th className="px-3 py-2 text-right font-medium">Hours</th>
                    <th className="px-3 py-2 font-medium">Pulled</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map((m) => (
                    <tr key={m.month} className="border-b border-border last:border-0">
                      <td className="px-3 py-1.5">{monthLabel(m.month)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatCount(m.rows)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatHours(m.seconds)}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">
                        {m.pulledAt ? formatDateTime(m.pulledAt) : 'unknown'}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-muted/30 font-medium">
                    <td className="px-3 py-1.5">On disk</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatCount(diskRows)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatHours(diskSeconds)}</td>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground">{files.data?.directory}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {months.length > 0 && (
            /* Two tables on this page count the same hours into months on two
               different bases, and on the real export they disagree by an entry
               or two. Said out loud here, that difference is a midnight
               straddler; unsaid, it reads as a lost entry. */
            <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
              Months here are the windows the export was pulled in — UTC. Step 4 buckets the same entries in the
              workspace’s own timezone, so an entry logged near midnight on the first or last of a month can
              appear under its neighbour there. The totals agree; a month or two need not.
            </p>
          )}
          {(files.data?.warnings.length ?? 0) > 0 && (
            <ul className="space-y-1 border-t border-border p-3 text-xs text-amber-700 dark:text-amber-400">
              {files.data!.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── The months ── */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label htmlFor="clockify-from" className="text-xs font-medium text-muted-foreground">
            From month
          </label>
          <Input
            id="clockify-from"
            type="month"
            className="w-40"
            value={fromMonth}
            onChange={(e) => onFromChange(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="clockify-to" className="text-xs font-medium text-muted-foreground">
            To month
          </label>
          <Input
            id="clockify-to"
            type="month"
            className="w-40"
            value={toMonth}
            onChange={(e) => onToChange(e.target.value)}
          />
        </div>
        <Button size="sm" className="gap-1.5 cursor-pointer" disabled={!rangeValid || scanning} onClick={onScan}>
          {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          {scanning ? 'Reading...' : preview ? 'Scan again' : 'Scan range'}
        </Button>
      </div>

      {!rangeValid && (
        <p className="text-xs text-muted-foreground">
          {isMonth(fromMonth) && isMonth(toMonth)
            ? 'The first month must not be after the last.'
            : 'Pick a first and a last month.'}
        </p>
      )}
      {rangeValid && (
        <p className="text-xs text-muted-foreground">
          Covers {rangeFromMonths(fromMonth, toMonth).from} to {rangeFromMonths(fromMonth, toMonth).to}, inclusive.
        </p>
      )}

      {scanError && <InlineError message={scanError} />}
    </StepCard>
  );
}

function SourceChoice({
  selected,
  onSelect,
  title,
  detail,
  note,
  disabled,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
  note: string;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        'rounded-md border p-3 text-left transition-colors',
        selected ? 'border-hygge bg-hygge/5' : 'border-border hover:bg-muted/40',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{title}</span>
        {selected && <Check className="h-3.5 w-3.5 text-hygge" />}
      </div>
      <p className="mt-0.5 text-xs text-foreground/80">{detail}</p>
      <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>
    </button>
  );
}

// ─── Step 3 · Map ──────────────────────────────────────────────────────────

function MapStep({
  preview,
  options,
  optionsLoading,
  optionsError,
  projectDraft,
  userDraft,
  onProjectChange,
  onUserChange,
  saving,
  readOnly,
  saveError,
  onSave,
}: {
  preview: ClockifyImportPreview | null;
  options: ClockifyMappingOptions | undefined;
  optionsLoading: boolean;
  readOnly: boolean;
  optionsError: string | null;
  projectDraft: DraftMap;
  userDraft: DraftMap;
  onProjectChange: (clockifyId: string, value: string) => void;
  onUserChange: (clockifyId: string, value: string) => void;
  saving: boolean;
  saveError: string | null;
  onSave: (payload: SaveMappingsPayload) => void;
}) {
  const projectOptions = useMemo(
    () =>
      (options?.projects ?? []).map((p) => ({
        id: p.id,
        name: p.status && p.status.toLowerCase() !== 'active' ? `${p.name} (${p.status.toLowerCase()})` : p.name,
      })),
    [options],
  );
  const employeeOptions = useMemo(
    () =>
      (options?.employees ?? []).map((e) => ({
        id: e.id,
        // The email is in the label because the automatic match is BY email —
        // seeing it is how a person checks the one the system could not make.
        name: `${e.firstName} ${e.lastName} · ${e.workEmail}`,
      })),
    [options],
  );

  if (!preview) {
    return (
      <StepCard
        step={3}
        title="Map projects and people"
        state="todo"
        blockedReason="Scan a range first — the projects and people to map are whatever appears in it."
      />
    );
  }

  const projectRows = preview.mappings.projects;
  const userRows = preview.mappings.users;
  const hoursByProject = new Map(preview.byClockifyProject.map((g) => [g.id, g]));
  const hoursByUser = new Map(preview.byClockifyUser.map((g) => [g.id, g]));

  const projectValue = (m: ClockifyProjectMapping) =>
    projectDraft[m.clockifyProjectId] ?? valueOf({ decision: m.decision, target: m.projectId });
  const userValue = (m: ClockifyUserMapping) =>
    userDraft[m.clockifyUserId] ?? valueOf({ decision: m.decision, target: m.employeeId });

  // Only what the person actually changed is sent. Saving the untouched rows
  // would freeze the email matches into stored decisions, turning "the system
  // worked this out" into "a human said so" for 24 people who were never asked.
  const changedProjects = projectRows.filter((m) => projectValue(m) !== valueOf({ decision: m.decision, target: m.projectId }));
  const changedUsers = userRows.filter((m) => userValue(m) !== valueOf({ decision: m.decision, target: m.employeeId }));
  const dirty = changedProjects.length + changedUsers.length;

  const projectCounts = countDecisions(projectRows.map((m) => ({ decision: decisionOf(projectValue(m)).decision })));
  const userCounts = countDecisions(userRows.map((m) => ({ decision: decisionOf(userValue(m)).decision })));
  const allDecided = projectCounts.pending === 0 && userCounts.pending === 0;

  const save = () => {
    const payload: SaveMappingsPayload = {
      projects: changedProjects.map((m) => {
        const { decision, target } = decisionOf(projectValue(m));
        return {
          clockifyProjectId: m.clockifyProjectId,
          clockifyProjectName: m.clockifyProjectName,
          decision,
          ...(decision === 'map' ? { projectId: target } : {}),
        };
      }),
      users: changedUsers.map((m) => {
        const { decision, target } = decisionOf(userValue(m));
        return {
          clockifyUserId: m.clockifyUserId,
          clockifyUserName: m.clockifyUserName,
          ...(m.clockifyUserEmail ? { clockifyUserEmail: m.clockifyUserEmail } : {}),
          decision,
          ...(decision === 'map' ? { employeeId: target } : {}),
        };
      }),
    };
    onSave(payload);
  };

  return (
    <StepCard
      step={3}
      title="Map projects and people"
      state={allDecided ? 'done' : 'in_progress'}
      description="Every Clockify project and person in the range needs an answer. “Skip these hours” is an answer; leaving it blank is not."
      action={
        readOnly ? undefined : (
          <Button size="sm" className="gap-1.5 cursor-pointer" disabled={dirty === 0 || saving} onClick={save}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {dirty === 0 ? 'Saved' : `Save ${dirty} change${dirty === 1 ? '' : 's'}`}
          </Button>
        )
      }
    >
      {optionsLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading our projects and employees...
        </p>
      )}
      {optionsError && <InlineError message={optionsError} />}
      {saveError && <InlineError message={saveError} />}

      <MappingTable
        title="Clockify projects"
        readOnly={readOnly}
        counts={projectCounts}
        emptyText="No Clockify projects appear in this range."
        rows={projectRows.map((m) => {
          const hours = hoursByProject.get(m.clockifyProjectId);
          const value = projectValue(m);
          return {
            key: m.clockifyProjectId,
            name: m.clockifyProjectName,
            sub: null,
            rows: hours?.rows ?? 0,
            seconds: hours?.seconds ?? 0,
            value,
            options: projectOptions,
            placeholder: 'Choose a project...',
            hint:
              value === '' && m.suggestedProjectId
                ? `A project of ours has the same name — check it before using it: ${
                    projectOptions.find((p) => p.id === m.suggestedProjectId)?.name ?? m.suggestedProjectId
                  }`
                : null,
            suggestion: value === '' ? m.suggestedProjectId : null,
            onChange: (v: string) => onProjectChange(m.clockifyProjectId, v),
            badge: null,
          };
        })}
      />

      <MappingTable
        title="Clockify people"
        readOnly={readOnly}
        counts={userCounts}
        emptyText="No Clockify users appear in this range."
        rows={userRows.map((m) => {
          const hours = hoursByUser.get(m.clockifyUserId);
          return {
            key: m.clockifyUserId,
            name: m.clockifyUserName,
            sub: m.clockifyUserEmail,
            rows: hours?.rows ?? 0,
            seconds: hours?.seconds ?? 0,
            value: userValue(m),
            options: employeeOptions,
            placeholder: 'Choose an employee...',
            hint: null,
            suggestion: null,
            onChange: (v: string) => onUserChange(m.clockifyUserId, v),
            badge: m.matchedByEmail && userDraft[m.clockifyUserId] === undefined ? 'matched by email' : null,
          };
        })}
      />

      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div className="space-y-1 text-xs text-muted-foreground">
          <p>
            A person matched by email was matched automatically, on their Clockify address against
            their work email. Changing one records your decision instead, and your decision wins from
            then on.
          </p>
          <p>Decisions are remembered per workspace — they survive a re-scan, a reload and a disconnect.</p>
        </div>
      </div>
    </StepCard>
  );
}

interface MappingRowView {
  key: string;
  name: string;
  sub: string | null;
  rows: number;
  seconds: number;
  value: string;
  options: Array<{ id: string; name: string }>;
  placeholder: string;
  hint: string | null;
  suggestion: string | null;
  onChange: (value: string) => void;
  badge: string | null;
}

function MappingTable({
  title,
  counts,
  rows,
  emptyText,
  readOnly,
}: {
  title: string;
  counts: { total: number; mapped: number; skipped: number; pending: number };
  rows: MappingRowView[];
  emptyText: string;
  readOnly: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium">{title}</h3>
        <span className="text-xs text-muted-foreground">
          {counts.mapped} mapped · {counts.skipped} skipped ·{' '}
          <span className={cn(counts.pending > 0 && 'font-medium text-amber-700 dark:text-amber-400')}>
            {counts.pending} undecided
          </span>
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[42rem] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">In Clockify</th>
                <th className="px-3 py-2 text-right font-medium">Entries</th>
                <th className="px-3 py-2 text-right font-medium">Hours</th>
                <th className="px-3 py-2 font-medium">Import as</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const undecided = row.value === '';
                return (
                  <tr key={row.key} className={cn('border-b border-border last:border-0', undecided && 'bg-amber-500/5')}>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-medium">{row.name}</span>
                        {row.badge && (
                          <Badge variant="outline" className="px-1.5 text-[10px] text-muted-foreground">
                            {row.badge}
                          </Badge>
                        )}
                      </div>
                      {row.sub && <p className="text-xs text-muted-foreground">{row.sub}</p>}
                    </td>
                    <td className="px-3 py-2 text-right align-top tabular-nums">{formatCount(row.rows)}</td>
                    <td className="px-3 py-2 text-right align-top tabular-nums">{formatHours(row.seconds)}</td>
                    <td className="px-3 py-2 align-top">
                      {readOnly ? (
                        // A reader sees the decision, not a control that lies
                        // about being answerable — same rule as a colleague's
                        // rows on a task's Time Tracked list.
                        <span className={cn('text-sm', undecided && 'text-muted-foreground')}>
                          {row.value === SKIP_OPTION
                            ? 'Skip these hours'
                            : (row.options.find((o) => o.id === row.value)?.name ?? '— not decided —')}
                        </span>
                      ) : (
                        <SearchableSelect
                          value={row.value}
                          onChange={row.onChange}
                          options={[{ id: SKIP_OPTION, name: 'Skip these hours' }, ...row.options]}
                          allowNone
                          noneLabel="— not decided —"
                          placeholder={row.placeholder}
                          searchPlaceholder="Type to filter..."
                          ariaLabel={`Import ${row.name} as`}
                          className="w-72"
                        />
                      )}
                      {row.value === SKIP_OPTION && (
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {formatHours(row.seconds)} h will not be imported.
                        </p>
                      )}
                      {!readOnly && row.hint && (
                        <button
                          type="button"
                          className="mt-1 block cursor-pointer text-left text-[11px] text-primary hover:underline"
                          onClick={() => row.suggestion && row.onChange(row.suggestion)}
                        >
                          {row.hint}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Step 4 · Preview, and Step 5 · Import ─────────────────────────────────

function ImportStep({
  preview,
  payload,
  importing,
  importError,
  readOnly,
  onImport,
  run,
  runNote,
  onUndone,
}: {
  preview: ClockifyImportPreview | null;
  payload: PreviewPayload | null;
  importing: boolean;
  importError: string | null;
  readOnly: boolean;
  onImport: (payload: ImportPayload) => void;
  run: ClockifyImportRunItem | null;
  runNote: string | null;
  onUndone: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const blocked = importBlockedReason(preview);

  return (
    <>
      <PreviewStep preview={preview} />

      <StepCard
        step={5}
        title="Import"
        state={run ? 'done' : preview?.canImport ? 'in_progress' : 'todo'}
        blockedReason={preview ? undefined : 'Scan a range first.'}
        description="This is the only step that writes anything. Every run can be undone."
        action={
          readOnly ? undefined : (
            <Button
              size="sm"
              className="gap-1.5 cursor-pointer"
              disabled={!preview || !preview.canImport || importing || !payload}
              onClick={() => setConfirmOpen(true)}
            >
              {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {importing ? 'Writing...' : 'Import into Time Tracker'}
            </Button>
          )
        }
      >
        {blocked && <p className="text-xs text-muted-foreground">{blocked}</p>}
        {importError && <InlineError message={importError} />}
        {runNote && (
          <p className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">{runNote}</p>
        )}
        {run && <RunReport run={run} onUndone={onUndone} readOnly={readOnly} defaultExpanded />}
      </StepCard>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Import these hours?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">{preview ? describeImport(preview) : ''}</p>
                <p>
                  Bucketed in {preview?.timeZone}, read from{' '}
                  {preview?.source === 'files' ? 'the export files on disk' : 'the Clockify API'}.
                </p>
                <p>
                  Entries are written as approved, marked{' '}
                  <span className="font-mono text-xs">clockify</span>, and carry the Clockify id they
                  came from — so a second run over this range writes nothing, and this run can be
                  undone in one click.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                // The digest of the preview being read RIGHT NOW travels with
                // the request. The server re-resolves the mapping when it runs,
                // so this is what makes the figures above the figures written —
                // a decision re-saved in another tab comes back as a refusal
                // asking for a fresh scan, not as a silent difference.
                if (payload && preview) onImport({ ...payload, confirm: preview.confirm });
              }}
            >
              Import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function PreviewStep({ preview }: { preview: ClockifyImportPreview | null }) {
  const [copied, setCopied] = useState(false);

  if (!preview) {
    return (
      <StepCard
        step={4}
        title="Check the numbers"
        state="todo"
        blockedReason="Scan a range first. This is where its hours appear, per person and per month, to compare against Clockify."
      />
    );
  }

  const pivot = buildPivot(preview);
  /**
   * The per-cell "…would actually be written" figure, only when some of it
   * would. Before anything is mapped every cell would import nothing, and a grid
   * where all 150 cells carry an amber "0.00 in" says nothing the headline
   * figure has not already said louder — it just makes the numbers harder to
   * read against Clockify, which is the one thing this table is for.
   */
  const showDelta = preview.rowsToImport > 0;
  const tsv = pivotToTsv(pivot, {
    from: preview.from,
    to: preview.to,
    timeZone: preview.timeZone,
    source: preview.source,
  });

  const copy = () => {
    navigator.clipboard?.writeText(tsv).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => setCopied(false),
    );
  };

  return (
    <StepCard
      step={4}
      title="Check the numbers"
      state="done"
      description={`${preview.from} to ${preview.to}, bucketed in ${preview.timeZone}, read from ${preview.source === 'files' ? 'the export files on disk' : 'the Clockify API'}.`}
      action={
        <Button variant="outline" size="sm" className="gap-1.5 cursor-pointer" onClick={copy}>
          {copied ? <Check className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />}
          {copied ? 'Copied' : 'Copy table'}
        </Button>
      }
    >
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="Entries in range" value={formatCount(preview.rowsScanned)} note="Compare against Clockify" />
        <Figure label="Hours in range" value={formatHours(pivot.grand.seconds)} note="Every entry, exclusions included" />
        <Figure label="Would be written" value={`${formatCount(preview.rowsToImport)} entries`} note={`${formatHours(preview.secondsToImport)} hours`} />
        <Figure
          label="Lands on"
          value={`${formatCount(preview.employeesAffected)} people`}
          note={`${formatCount(preview.projectsAffected)} of our projects`}
        />
      </div>

      {preview.warnings.length > 0 && (
        <ul className="space-y-1 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400">
          {preview.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      {/* ── The grid ── */}
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full min-w-[40rem] text-sm">
          <thead>
            <tr className="border-b border-border text-xs text-muted-foreground">
              <th className="sticky left-0 bg-card px-3 py-2 text-left font-medium">Person</th>
              {pivot.months.map((m) => (
                <th key={m} className="px-3 py-2 text-right font-medium whitespace-nowrap">
                  {monthLabel(m)}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-medium">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {pivot.rows.map((row) => (
              <tr key={row.id} className="border-b border-border last:border-0">
                <td
                  className={cn(
                    'sticky left-0 bg-card px-3 py-1.5 whitespace-nowrap',
                    row.unattributed && 'italic text-muted-foreground',
                  )}
                >
                  {row.name}
                </td>
                {row.cells.map((cell, i) => (
                  <td key={pivot.months[i]} className="px-3 py-1.5 text-right tabular-nums">
                    {cell.seconds === 0 ? <span className="text-muted-foreground/40">—</span> : formatHours(cell.seconds)}
                    {showDelta && cell.seconds !== cell.importSeconds && (
                      <span className="block text-[11px] text-amber-700 dark:text-amber-400">
                        {formatHours(cell.importSeconds)} in
                      </span>
                    )}
                  </td>
                ))}
                <td className="px-3 py-1.5 text-right font-medium tabular-nums">{formatHours(row.total.seconds)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-muted/30 font-medium">
              <td className="sticky left-0 bg-muted/30 px-3 py-1.5">TOTAL hours</td>
              {pivot.totals.map((t, i) => (
                <td key={pivot.months[i]} className="px-3 py-1.5 text-right tabular-nums">
                  {formatHours(t.seconds)}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right tabular-nums">{formatHours(pivot.grand.seconds)}</td>
            </tr>
            <tr className="bg-muted/30 text-xs text-muted-foreground">
              <td className="sticky left-0 bg-muted/30 px-3 py-1.5">entries</td>
              {pivot.totals.map((t, i) => (
                <td key={pivot.months[i]} className="px-3 py-1.5 text-right tabular-nums">
                  {formatCount(t.rows)}
                </td>
              ))}
              <td className="px-3 py-1.5 text-right tabular-nums">{formatCount(pivot.grand.rows)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        {showDelta
          ? 'An amber figure under a cell is what would actually be written, where that is less than the hours Clockify holds.'
          : 'Nothing in this range would be written yet — the figures below are what Clockify holds.'}{' '}
        Months are bucketed in {preview.timeZone}, not in UTC — so a month here can differ by an entry from the
        per-file table in step 2, which lists the UTC windows the export was pulled in.
      </p>

      <details className="rounded-md border border-border">
        <summary className="cursor-pointer px-3 py-2 text-xs text-muted-foreground hover:text-foreground">
          Show the table as text, to paste into a spreadsheet
        </summary>
        <pre className="max-h-72 overflow-auto border-t border-border p-3 text-[11px] leading-relaxed">{tsv}</pre>
      </details>

      {/* ── Exclusions ── */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium">What is being left out</h3>
        {preview.skipped.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing. Every entry in this range would be written.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Reason</th>
                  <th className="px-3 py-2 text-right font-medium">Entries</th>
                  <th className="px-3 py-2 text-right font-medium">Hours</th>
                  <th className="px-3 py-2 font-medium">Clockify ids</th>
                </tr>
              </thead>
              <tbody>
                {preview.skipped.map((s) => (
                  <tr key={s.reason} className="border-b border-border last:border-0">
                    <td className="px-3 py-1.5">
                      <span className={cn(isBlockingReason(s.reason) && 'text-amber-700 dark:text-amber-400')}>
                        {skipReasonLabel(s.reason)}
                      </span>
                      {isBlockingReason(s.reason) && (
                        <span className="ml-1.5 text-xs text-muted-foreground">— blocks the import</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatCount(s.rows)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatHours(s.seconds)}</td>
                    <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground">
                      {s.sampleIds.slice(0, 3).join(', ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </StepCard>
  );
}

function Figure({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums">{value}</p>
      <p className="text-[11px] text-muted-foreground">{note}</p>
    </div>
  );
}

// ─── History ───────────────────────────────────────────────────────────────

function HistorySection({ onUndone, readOnly }: { onUndone: () => void; readOnly: boolean }) {
  const { data: runs, isLoading, isError, error } = useClockifyRuns();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Import history</CardTitle>
        <CardDescription>Every run, newest first — including the ones that were undone</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading...
          </p>
        )}
        {isError && <InlineError message={extractErrorMessage(error, 'Could not load the run history')} />}
        {runs && runs.length === 0 && (
          <p className="py-2 text-sm text-muted-foreground">
            Nothing has been imported yet. Scan a range and check its numbers first — this page writes
            nothing until you press Import.
          </p>
        )}
        {(runs ?? []).map((r) => {
          const expanded = expandedId === r.id;
          return (
            <div key={r.id} className="border-b border-border py-2 last:border-0">
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : r.id)}
                className="flex w-full cursor-pointer items-center gap-3 text-left"
              >
                <ChevronRight
                  className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')}
                />
                <RunStatusBadge status={r.status} />
                <span className="truncate text-xs text-muted-foreground">
                  {r.from} → {r.to} · {r.source === 'files' ? 'from disk' : 'from API'}
                </span>
                <span className="hidden text-xs text-muted-foreground sm:inline">
                  {formatCount(r.counts.inserted ?? 0)} entries · {formatHours(r.counts.secondsInserted ?? 0)} h
                </span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">{formatDateTime(r.startedAt)}</span>
              </button>
              {expanded && (
                <div className="ml-6 mt-2">
                  <RunReport run={r} onUndone={onUndone} readOnly={readOnly} />
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

const RUN_BADGES: Record<ClockifyImportRunItem['status'], { label: string; className: string }> = {
  completed: { label: 'Completed', className: 'text-green-600 border-green-600/30 bg-green-600/5' },
  running: { label: 'Running', className: 'text-blue-500 border-blue-500/30 bg-blue-500/5' },
  failed: { label: 'Failed', className: 'text-destructive border-destructive/30 bg-destructive/5' },
  undone: { label: 'Undone', className: 'text-muted-foreground border-border bg-muted/50' },
};

function RunStatusBadge({ status }: { status: ClockifyImportRunItem['status'] }) {
  // Fall back to the raw status so an unknown value never blanks the row.
  const cfg = RUN_BADGES[status] ?? { label: status, className: '' };
  return (
    <Badge variant="outline" className={cn('shrink-0 px-1.5 text-[10px]', cfg.className)}>
      {cfg.label}
    </Badge>
  );
}

function RunReport({
  run,
  onUndone,
  readOnly,
  defaultExpanded = false,
}: {
  run: ClockifyImportRunItem;
  onUndone: () => void;
  readOnly: boolean;
  defaultExpanded?: boolean;
}) {
  const undo = useUndoClockifyRun();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const counts = run.counts ?? { inserted: 0, secondsInserted: 0, rowsScanned: 0, rowsPlanned: 0, employeesAffected: 0, projectsAffected: 0 };
  /**
   * This panel is handed a run record fetched before the undo. Once THIS
   * component has undone it, the record on screen is stale in the one way that
   * matters — a green "Completed" over rows that are no longer there.
   */
  const status = undo.isSuccess ? 'undone' : run.status;
  const canUndo = !readOnly && status === 'completed' && (counts.inserted ?? 0) > 0;

  return (
    <div className={cn('space-y-3 rounded-md border border-border p-3', defaultExpanded && 'bg-muted/30')}>
      <div className="flex flex-wrap items-center gap-2">
        <RunStatusBadge status={status} />
        <span className="text-sm font-medium">
          {plural(counts.inserted ?? 0, 'entry', 'entries')} written · {formatHours(counts.secondsInserted ?? 0)} hours
        </span>
        <span className="text-xs text-muted-foreground">
          {formatCount(counts.rowsScanned ?? 0)} scanned, {formatCount(counts.rowsPlanned ?? 0)} planned
        </span>
        {canUndo && (
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer gap-1.5 sm:ml-auto"
            disabled={undo.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            {undo.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
            Undo this run
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        {run.from} → {run.to}, bucketed in {run.timeZone}, read from{' '}
        {run.source === 'files' ? 'the export files on disk' : 'the Clockify API'}. Entries written as{' '}
        <span className="font-mono">{run.entryStatus}</span>. Started{' '}
        {formatDateTime(run.startedAt)}
        {run.startedBy ? ` by ${run.startedBy.firstName} ${run.startedBy.lastName}` : ''}
        {run.undoneAt
          ? ` · undone ${formatDateTime(run.undoneAt)}${run.undoneBy ? ` by ${run.undoneBy.firstName} ${run.undoneBy.lastName}` : ''}`
          : ''}
        .
      </p>

      {run.error && <InlineError message={run.error} />}
      {undo.isError && <InlineError message={extractErrorMessage(undo.error, 'Could not undo this run')} />}
      {undo.data && (
        <p className="text-xs text-muted-foreground">
          Removed {formatCount(undo.data.deleted)} of {formatCount(undo.data.expected)} entries.
          {undo.data.note ? ` ${undo.data.note}` : ''}
        </p>
      )}

      {run.skipped.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium">Left out</p>
          <ul className="space-y-0.5 text-xs text-muted-foreground">
            {run.skipped.map((s) => (
              <li key={s.reason}>
                {skipReasonLabel(s.reason)}: {plural(s.rows, 'entry', 'entries')}, {formatHours(s.seconds)} h
              </li>
            ))}
          </ul>
        </div>
      )}

      {run.warnings.length > 0 && (
        <ul className="space-y-0.5 text-xs text-amber-700 dark:text-amber-400">
          {run.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this import?</AlertDialogTitle>
            <AlertDialogDescription>
              Deletes exactly the {plural(counts.inserted ?? 0, 'time entry', 'time entries')} this run wrote —{' '}
              {formatHours(counts.secondsInserted ?? 0)} hours — and nothing else. Entries typed by
              hand and entries from other runs are untouched. The mapping decisions are kept, so the
              range can be re-imported straight away.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                undo.mutate(run.id, { onSuccess: onUndone });
              }}
            >
              Undo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Shared ────────────────────────────────────────────────────────────────

function InlineError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
      <p className="text-xs text-destructive">{message}</p>
    </div>
  );
}
